import React, {useState} from 'react';

import Image from 'next/image';
import {useRouter} from 'next/router';
import {useIntl} from 'react-intl';
import styled from 'styled-components';

import {colors, dimensions} from '@stryd/react-ui';
import {isNumber, stripeAmountToCurrencyAmount} from '@stryd/util-lib';

import {regionConfigs} from 'src/config';
import {usePageRegionData} from 'src/contexts';
import {useCart} from 'src/features/cart/contexts';
import {CartTestType} from 'src/features/cart/types';
import {getVariantPricing, VariantPricing} from 'src/features/pricing/helpers';
import {MoneyBag} from 'src/features/pricing/types';
import {
  getStryd5ContentfulProduct,
  getStryd5MonthlyContentfulProduct,
  getStryd5AnnualContentfulProduct,
  getStryd5DuoContentfulProduct,
  getStryd5DuoMonthlyContentfulProduct,
  getStryd5DuoAnnualContentfulProduct,
  getStryd5ClipContentfulProduct,
  getStryd5ClipAddOnContentfulProduct,
} from 'src/features/product/data';
import {usePriceFormatters} from 'src/hooks/usePriceFormatters';
import {addToCartEvent} from 'src/utils/analytics/events';
import {track} from 'src/utils/analytics/gtag';
import {variantToItems} from 'src/utils/analytics/variantToItems';
import {IProduct} from 'src/utils/contentful';

import CheckoutSummary from './CheckoutSummary';
import ClipsSection from './ClipsSection';
import CommitmentSelector from './CommitmentSelector';
import ProductImageCarousel from './ProductImageCarousel';
import ProductSelector from './ProductSelector';
import {getProductMedia, MediaItem} from './productMediaConfig';

const columnLayoutBreakpoint = dimensions.breakpoints.tabletLandscape;

const StoreLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 80vh;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    flex-direction: row;
  }
`;

const CarouselSection = styled.div`
  width: 100%;
  order: 1;
  flex: 1;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    width: 100%;
    order: 1;
    flex: 0 0 50%;
  }
`;

const ProductSection = styled.div`
  width: 100%;
  max-width: 100%;
  order: 2;
  flex: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    width: 100%;
    order: 2;
    flex: 0 0 50%;
    overflow-y: auto;
    max-height: 90vh;
  }
`;

export const StoreHero: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [selectedProduct, setSelectedProduct] = useState('stryd-pod');
  const [selectedCommitment, setSelectedCommitment] =
    useState('monthly-commitment');
  const [includeClips, setIncludeClips] = useState(false);
  const {locale, region} = usePageRegionData();
  const {currency} = regionConfigs[region];
  const {formatOneTimePrice} = usePriceFormatters({locale});
  const [cart, cartDispatch] = useCart();
  const router = useRouter();
  const intl = useIntl();

  // Get product data
  const stryd5Product = getStryd5ContentfulProduct({locale});
  const stryd5MonthlyProduct = getStryd5MonthlyContentfulProduct({locale});
  const stryd5AnnualProduct = getStryd5AnnualContentfulProduct({locale});
  const stryd5DuoProduct = getStryd5DuoContentfulProduct({locale});
  const stryd5DuoMonthlyProduct = getStryd5DuoMonthlyContentfulProduct({
    locale,
  });
  const stryd5DuoAnnualProduct = getStryd5DuoAnnualContentfulProduct({locale});
  const stryd5ClipProduct = getStryd5ClipContentfulProduct({locale});
  const stryd5ClipAddOnProduct = getStryd5ClipAddOnContentfulProduct({locale});

  // Get pricing
  const stryd5Pricing = getVariantPricing({
    variant: stryd5Product.fields.variants[0],
    region,
    currency,
  });
  const stryd5MonthlyPricing = getVariantPricing({
    variant: stryd5MonthlyProduct.fields.variants[0],
    region,
    currency,
  });
  const stryd5AnnualPricing = getVariantPricing({
    variant: stryd5AnnualProduct.fields.variants[0],
    region,
    currency,
  });
  const stryd5DuoPricing = getVariantPricing({
    variant: stryd5DuoProduct.fields.variants[0],
    region,
    currency,
  });
  const stryd5DuoMonthlyPricing = getVariantPricing({
    variant: stryd5DuoMonthlyProduct.fields.variants[0],
    region,
    currency,
  });
  const stryd5DuoAnnualPricing = getVariantPricing({
    variant: stryd5DuoAnnualProduct.fields.variants[0],
    region,
    currency,
  });
  const stryd5ClipPricing = getVariantPricing({
    variant: stryd5ClipProduct.fields.variants[0],
    region,
    currency,
  });
  const stryd5ClipAddOnPricing = getVariantPricing({
    variant: stryd5ClipAddOnProduct.fields.variants[0],
    region,
    currency,
  });

  // Product options for selector
  const productOptions = [
    {
      id: 'stryd-pod',
      name: 'Stryd 5.0',
      included: '1x Stryd 5.0',
      description: [
        intl.formatMessage({id: 'store5.podDescription1'}),
        intl.formatMessage({id: 'store5.podDescription2'}),
        intl.formatMessage({id: 'store5.podDescription3'}),
      ],
      accent: '',
      isSelected: selectedProduct === 'stryd-pod',
      onClick: () => setSelectedProduct('stryd-pod'),
    },
    {
      id: 'stryd-duo',
      name: 'Stryd Duo 5.0',
      included: '2x Stryd 5.0',
      description: [
        intl.formatMessage({id: 'store5.duoDescription1'}),
        intl.formatMessage({id: 'store5.duoDescription2'}),
        intl.formatMessage({id: 'store5.duoDescription3'}),
      ],
      accent: intl.formatMessage({id: 'store5.duoAccent'}),
      isSelected: selectedProduct === 'stryd-duo',
      onClick: () => setSelectedProduct('stryd-duo'),
    },
  ];

  const productPriceOptions = {
    'stryd-pod': {
      'no-membership': stryd5Pricing,
      'monthly-commitment': stryd5MonthlyPricing,
      'yearly-upfront': stryd5AnnualPricing,
    },
    'stryd-duo': {
      'no-membership': stryd5DuoPricing,
      'monthly-commitment': stryd5DuoMonthlyPricing,
      'yearly-upfront': stryd5DuoAnnualPricing,
    },
  };

  const commitmentOptions = [
    {
      id: 'no-membership',
      name: intl.formatMessage({id: 'store5.noMembershipName'}),
      description: [
        intl.formatMessage({id: 'store5.noMembershipDescription1'}),
        intl.formatMessage({id: 'store5.noMembershipDescription2'}),
      ],
      price: '',
      priceHelper: '',
      accent: '',
      isSelected: selectedCommitment === 'no-membership',
      onClick: () => setSelectedCommitment('no-membership'),
    },
    {
      id: 'monthly-commitment',
      name: intl.formatMessage({id: 'store5.monthlyCommitmentName'}),
      description: [
        intl.formatMessage({id: 'store5.monthlyCommitmentDescription1'}),
      ],
      price: intl.formatMessage(
        {id: 'store5.monthlyCommitmentPrice'},
        {
          price: formatOneTimePrice(
            productPriceOptions[
              selectedProduct as keyof typeof productPriceOptions
            ]['monthly-commitment'].recurringPricesTotals[0]
          ),
          timeSpan:
            productPriceOptions[
              selectedProduct as keyof typeof productPriceOptions
            ]['monthly-commitment'].recurringPricesTotals[0]
              .min_recurring_intervals,
        }
      ),
      priceHelper: intl.formatMessage({id: 'store5.noObligation'}),
      accent: '',
      isSelected: selectedCommitment === 'monthly-commitment',
      onClick: () => setSelectedCommitment('monthly-commitment'),
    },
    {
      id: 'yearly-upfront',
      name: intl.formatMessage({id: 'store5.yearlyCommitmentName'}),
      description: [
        intl.formatMessage({id: 'store5.yearlyCommitmentDescription1'}),
        intl.formatMessage({id: 'store5.yearlyCommitmentDescription2'}),
      ],
      price: intl.formatMessage(
        {id: 'store5.yearlyCommitmentPrice'},
        {
          price: formatOneTimePrice(
            productPriceOptions[
              selectedProduct as keyof typeof productPriceOptions
            ]['yearly-upfront'].recurringPricesTotals[0]
          ),
        }
      ),
      priceHelper: intl.formatMessage({id: 'store5.noObligation'}),
      accent: '',
      isSelected: selectedCommitment === 'yearly-upfront',
      onClick: () => setSelectedCommitment('yearly-upfront'),
    },
  ];

  // Get media for selected product
  const mediaItems = getProductMedia(selectedProduct);

  // Get selected product and commitment prices
  const selectedProductOption = productOptions.find((p) => p.isSelected);
  const selectedCommitmentOption = commitmentOptions.find((c) => c.isSelected);

  const handleAddToCart = (product: IProduct, includeClips: boolean) => {
    const variant = product.fields.variants[0];

    const itemInCart = cart.lineItems.find(
      (item) => item.selectedVariant.sys.id === variant.sys.id
    );

    cartDispatch({
      type: 'CLEAR_REGIONAL_CART',
      region,
    });

    cartDispatch({
      type: 'ADD_QTY_TO_CART',
      region,
      quantity: 1,
      item: {product: product, selectedVariant: variant},
    });

    if (includeClips) {
      cartDispatch({
        type: 'ADD_QTY_TO_CART',
        region,
        quantity: 1,
        item: {
          product: stryd5ClipAddOnProduct,
          selectedVariant: stryd5ClipAddOnProduct.fields.variants[0],
        },
      });
    }

    router.push(`/${region}/${locale}/cart`);
  };

  const handleCheckout = () => {
    // Add main product to cart
    if (
      selectedProduct === 'stryd-pod' &&
      selectedCommitment === 'no-membership'
    ) {
      handleAddToCart(stryd5Product, includeClips);
    } else if (
      selectedProduct === 'stryd-pod' &&
      selectedCommitment === 'monthly-commitment'
    ) {
      handleAddToCart(stryd5MonthlyProduct, includeClips);
    } else if (
      selectedProduct === 'stryd-pod' &&
      selectedCommitment === 'yearly-upfront'
    ) {
      handleAddToCart(stryd5AnnualProduct, includeClips);
    } else if (
      selectedProduct === 'stryd-duo' &&
      selectedCommitment === 'no-membership'
    ) {
      handleAddToCart(stryd5DuoProduct, includeClips);
    } else if (
      selectedProduct === 'stryd-duo' &&
      selectedCommitment === 'monthly-commitment'
    ) {
      handleAddToCart(stryd5DuoMonthlyProduct, includeClips);
    } else if (
      selectedProduct === 'stryd-duo' &&
      selectedCommitment === 'yearly-upfront'
    ) {
      handleAddToCart(stryd5DuoAnnualProduct, includeClips);
    }
  };

  return (
    <StoreLayout>
      <CarouselSection>
        <ProductImageCarousel
          mediaItems={mediaItems}
          selectedProduct={selectedProduct}
          enableScrollAdvancement={true}
        />
      </CarouselSection>

      <ProductSection>
        <ProductSelector
          products={productOptions}
          productPrices={productPriceOptions}
          selectedProduct={selectedProduct}
          selectedCommitment={selectedCommitment}
        />
        <CommitmentSelector
          commitments={commitmentOptions}
          productPrices={productPriceOptions}
          selectedProduct={selectedProduct}
          selectedCommitment={selectedCommitment}
        />

        <ClipsSection
          includeClips={includeClips}
          onClipsChange={setIncludeClips}
          clipPricing={stryd5ClipAddOnPricing}
          clipOriginalPricing={stryd5ClipPricing}
        />

        <CheckoutSummary
          onCheckout={handleCheckout}
          shippingTimeline={intl.formatMessage({id: 'store5.shippingTimeline'})}
          productPrices={productPriceOptions}
          selectedProduct={selectedProduct}
          selectedCommitment={selectedCommitment}
          includeClips={includeClips}
          clipPricing={stryd5ClipAddOnPricing}
          clipOriginalPricing={stryd5ClipPricing}
          productName={selectedProductOption?.name || 'Stryd 5.0'}
          className=""
        />
      </ProductSection>
    </StoreLayout>
  );
};
